package ai.jessie.shared.invoices

import kotlinx.serialization.Serializable

// UC-013 / FR-013. IDs are public UUIDs; amounts are exact decimal strings. Every amount, rate
// and total in a response is calculated by the server; request amounts are inputs, never totals.
@Serializable data class InvoiceWorkInput(val id: String, val revision: Long? = null)

// FREE: description, quantity, unitPrice and vatRateId. FIXED_PRICE and TIME_MATERIAL: projectId
// and positionId; TIME_MATERIAL also selects work. Description is optional for position lines.
@Serializable
data class InvoiceLineInput(
    val type: String,
    val description: String? = null,
    val quantity: String? = null,
    val unitPrice: String? = null,
    val vatRateId: String? = null,
    val projectId: String? = null,
    val positionId: String? = null,
    val work: List<InvoiceWorkInput> = emptyList(),
)

// The totals the member reviewed (A7/A4); the server compares, never adopts them.
@Serializable data class InvoiceTotalsInput(val net: String, val vat: String, val gross: String)

@Serializable
data class InvoiceDraftRequest(
    val customerId: String,
    val currency: String,
    val issueDate: String,
    val dueDate: String,
    val lines: List<InvoiceLineInput>,
    val reviewedTotals: InvoiceTotalsInput? = null,
)

@Serializable
data class InvoiceCustomerOption(val id: String, val name: String, val address: String)

@Serializable data class InvoiceVatOption(val id: String, val name: String, val rate: String)

@Serializable
data class InvoiceWorkOption(
    val id: String,
    val workDate: String,
    val durationMinutes: Int,
    val description: String?,
    val revision: Long,
)

@Serializable
data class InvoicePositionOption(
    val id: String,
    val name: String,
    val type: String,
    val unitPrice: String,
    val vatName: String,
    val vatRate: String,
    val work: List<InvoiceWorkOption>,
)

@Serializable
data class InvoiceProjectOption(
    val id: String,
    val name: String,
    val positions: List<InvoicePositionOption>,
)

// Projects and positions are present only for the organisation default currency (B2) and the
// selected eligible customer; time/material positions list their eligible unbilled work.
@Serializable
data class InvoiceOptionsResponse(
    val currentDate: String,
    val issueDate: String,
    val dueDate: String,
    val defaultCurrency: String,
    val currencies: List<String>,
    val customers: List<InvoiceCustomerOption>,
    val vatRates: List<InvoiceVatOption>,
    val projects: List<InvoiceProjectOption>,
)

@Serializable
data class InvoiceLineWorkResponse(
    val id: String,
    val workDate: String,
    val billedMinutes: Int,
    val revision: Long,
)

@Serializable
data class InvoiceLineResponse(
    val type: String,
    val description: String,
    val quantity: String,
    val unitPrice: String,
    val vatName: String,
    val vatRate: String,
    val totalNet: String,
    val totalVat: String,
    val totalGross: String,
    val projectId: String?,
    val positionId: String?,
    val work: List<InvoiceLineWorkResponse>,
)

@Serializable data class InvoiceTotalsResponse(val net: String, val vat: String, val gross: String)

@Serializable
data class InvoicePreviewResponse(
    val customerId: String,
    val recipientName: String,
    val recipientAddress: String,
    val currency: String,
    val issueDate: String,
    val dueDate: String,
    val lines: List<InvoiceLineResponse>,
    val totals: InvoiceTotalsResponse,
)

@Serializable
data class InvoiceResponse(
    val id: String,
    val status: String,
    val customerId: String,
    val recipientName: String,
    val recipientAddress: String,
    val currency: String,
    val issueDate: String,
    val dueDate: String,
    val lines: List<InvoiceLineResponse>,
    val totals: InvoiceTotalsResponse,
    val createdAt: String,
    val updatedAt: String,
    val number: String? = null,
    // UC-014 B3: dispatch and payment dates (ISO dates) once recorded.
    val sentOn: String? = null,
    val paidOn: String? = null,
)

@Serializable data class InvoiceFieldProblem(val field: String, val reason: String)

// WORK_CHANGED and TOTALS_CHANGED carry the refreshed preview (A3, A4, A7). UC-014
// TRANSITION_NOT_PERMITTED and STATE_CHANGED carry the invoice's latest progress (A7, A10).
@Serializable
data class InvoiceProblemResponse(
    val code: String,
    val problems: List<InvoiceFieldProblem> = emptyList(),
    val preview: InvoicePreviewResponse? = null,
    val progress: InvoiceProgressResponse? = null,
)
